<template>
  <view-wrapper
    loadingText="Order data"
    backText="Go to order list"
    prefix="Edit"
    name="Order"
    path="orders"
    :loading="loading"
  >
    <wrapper class="mb-5" :checkForChildFocused="true">
      <grafokett-input
        v-if="orderToBeEdited"
        v-model="orderToBeEdited.orderReference"
        type="text"
        labelPlaceholder="Order number"
        :inline-save="true"
        :editable="true"
        @inlineSave="updateOrderReference"
      />
    </wrapper>

    <wrapper class="mb-5">
      <div class="flex items-center justify-between">
        <p>Order Data</p>
        <button
          class="flex items-center px-4 py-2 font-bold bg-transparent border-solid rounded-lg cursor-pointer border-highlight text-highlight"
          type="button"
          @click="openAddOrderDataModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clip-rule="evenodd"
            />
          </svg>
          Add
        </button>
      </div>
      <div
        class="grid grid-cols-5 gap-4"
        v-for="(data, index) in orderData"
        :key="index"
      >
        <grafokett-input
          class="col-span-1"
          :editable="false"
          :readonly="true"
          type="text"
          v-model="data.key"
        />
        <div class="flex col-span-4 gap-4">
          <grafokett-input
            :editable="data.isEdit"
            :readonly="!data.isEdit"
            type="text"
            v-model="data.data"
            :is-expandable="true"
          />

          <div class="flex self-center flex-none text-highlight">
            <svg
              v-if="!data.isEdit"
              @click="data.isEdit = true"
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 cursor-pointer"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <svg
              v-if="data.isEdit"
              @click="updateOrderData(data)"
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 cursor-pointer"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 ml-2 cursor-pointer"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              @click="deleteOrderData(data.key)"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
        </div>
      </div>
    </wrapper>

    <wrapper :focus="addedArticles.length > 0" class="mb-4 articels">
      <div
        v-if="addedArticles && addedArticles.length > 0"
        class="added-articels"
      >
        <p>Articles in this order</p>
        <GenericAccordion
          :columnsToNotSort="columnsToNotSort"
          :types="types"
          :headers="addedArticlesHeaders"
          :data="addedArticles"
          @buttonClicked="removeArticle"
          :inputInlineSave="true"
          :useSearch="false"
          @inputInlineSave="updateOrderQuantity"
        />
        <br />
      </div>
      <div>
        <p>Add articles to order</p>
        <p v-if="articles && articles.length === 0">No matching articles</p>
        <p v-if="!articles">Loading articles...</p>
        <GenericAccordion
          :columnsToNotSort="columnsToNotSort"
          :types="types"
          :headers="headers"
          :data="articles"
          :useSearch="true"
          @searchQuery="handleSearch"
          @buttonClicked="addArticle"
        ></GenericAccordion>
      </div>
    </wrapper>

    <assign-groups
      :groups="groups"
      v-model="parentGroup"
      @groupAdded="addGroup"
      @removeGroup="removeGroup"
    />

    <modal @close="isAddOrderDataOpen = false" v-if="isAddOrderDataOpen">
      <template slot="header">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-24 h-24 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
            d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </template>
      <template slot="body">
        <div class="px-6 py-2 mb-4 rounded-lg bg-primary">
          <grafokett-input
            class="flex-1 mr-4"
            :editable="true"
            type="text"
            v-model="addOrderDataKey"
            labelPlaceholder="Key"
          />
        </div>

        <div class="px-6 py-2 rounded-lg bg-primary">
          <grafokett-input
            class="flex-1"
            :editable="true"
            type="text"
            v-model="addOrderDataData"
            labelPlaceholder="Data"
            :is-expandable="true"
          />
        </div>
      </template>
      <template slot="buttons">
        <button class="mt-4 button primary" @click="isAddOrderDataOpen = false">
          Cancel
        </button>
        <button
          :disabled="!addOrderDataKey || !addOrderDataKey.length"
          class="mt-4 ml-4 button primary"
          @click="addOrderData"
        >
          Save
        </button>
      </template>
    </modal>
  </view-wrapper>
</template>

<script>
import GrafokettInput from "../components/forms/Input.vue";
import GenericAccordion from "../components/GenericAccordion";
import AssignGroups from "../components/datacomponents/AssignGroups";
import Modal from "../components/Modal.vue";
export default {
  components: {
    GrafokettInput,
    GenericAccordion,
    AssignGroups,
    Modal,
  },
  data() {
    return {
      orderToBeEdited: undefined,
      orderNumber: "",
      showPopUp: false,
      addedArticles: [],
      orderData: [],
      articles: null,
      parentGroup: [],
      articlesLockedIn: false,
      groupsLockedIn: false,
      isAddOrderDataOpen: false,
      addedArticlesHeaders: {
        article: "Article name",
        quantity: "Maximum print quantity",
        remove: "",
      },
      headers: {
        article: "Article name",
        quantity: "Maximum print quantity",
        add: "",
      },
      columnsToNotSort: ["quantity", "add", "remove"],
      types: {
        article: { type: "string" },
        quantity: { type: "number-input" },
        add: { type: "button", classes: "small", textAlign: "right" },
        remove: { type: "button", classes: "small", textAlign: "right" },
      },
      loading: false,
      addOrderDataKey: "",
      addOrderDataData: "",
    };
  },
  methods: {
    hasChangesBeenMade() {
      return (
        this.orderNumber !== "" ||
        this.addedArticles.length > 0 ||
        this.parentGroup.length > 0
      );
    },
    async updateOrderReference() {
      this.loading = true;
      await this.$store.$api.put("/api/orders/order-reference", {
        orderId: this.orderToBeEdited.id,
        orderReference: this.orderToBeEdited.orderReference,
      });
      this.loading = false;
    },
    async updateOrderQuantity(article) {
      this.loading = true;
      await this.$store.$api.put("/api/orders/articles/max-quantity", {
        orderId: this.orderToBeEdited.id,
        articleId: article.id,
        maxQuantity: parseInt(article.quantity),
      });
      this.loading = false;
    },
    async addGroup(group) {
      this.loading = true;
      await this.$store.$api.post("/api/orders/groups", {
        orderId: this.orderToBeEdited.id,
        groupId: group.id,
      });
      this.loading = false;
    },
    async removeGroup(group) {
      this.loading = true;
      await this.$store.$api.post("/api/orders/groups/remove", {
        orderId: this.orderToBeEdited.id,
        groupId: group.groupId ? group.groupId : group.id,
      });
      this.loading = false;
    },
    async getData() {
      this.loading = true;
      const response = await this.$store.$api.get(
        `/api/orders/${this.$route.params.orderId}`
      );
      await this.$store.dispatch("GROUPS/GET_AVAILABLE_GROUPS");
      await this.$store.dispatch("ARTICLES/GET_ARTICLES");
      this.orderToBeEdited = response.data.data;
      this.addedArticles = this.orderToBeEdited.articles.map(a => {
        return {
          article: a.name,
          quantity: a.maxQuantity ? a.maxQuantity.toString() : null,
          add: "Add",
          remove: "Remove",
          id: a.id,
        };
      });

      this.orderToBeEdited.groups.forEach(group => {
        this.addParentGroup(group);
      });
      await this.searchArticles("", 1, 10);

      this.orderData = this.orderToBeEdited.orderData.map(({ key, data }) => ({
        key,
        data,
        isEdit: false,
      }));

      this.loading = false;
    },
    async searchArticles(searchQuery, page, pageSize) {
      this.articles = null;
      const response = await this.$store.$api.get(
        `/api/articles?take=${pageSize}&offset=${pageSize *
          (page - 1)}&searchQuery=${searchQuery}`
      );
      this.articles = response.data.data
        .map(a => {
          return {
            article: a.name,
            quantity: "80",
            add: "Add",
            remove: "Remove",
            id: a.id,
          };
        })
        .filter(a => !this.addedArticles.find(x => x.id === a.id));
    },
    async handleSearch(value) {
      await this.searchArticles(value, 1, 1000);
    },
    async addArticle(e) {
      if (e.button === "add") {
        if (!this.addedArticles.find(x => x.id === e.data.id)) {
          this.loading = true;
          await this.$store.$api.post("/api/orders/articles", {
            orderId: this.orderToBeEdited.id,
            articleId: e.data.id,
            maxQuantity: parseInt(e.data.quantity),
          });
          this.addedArticles.push(e.data);
          this.loading = false;
        }
      }
    },
    async removeArticle(e) {
      if (e.button === "remove") {
        this.loading = true;
        await this.$store.$api.post("/api/orders/articles/remove", {
          orderId: this.orderToBeEdited.id,
          articleId: e.data.id,
        });
        this.addedArticles = this.addedArticles.filter(x => x.id !== e.data.id);
        this.loading = false;
      }
    },
    addParentGroup(group) {
      if (this.doesGroupExist(group, this.parentGroup)) return;
      const i = this.parentGroup.indexOf(group);
      if (i === -1 && !this.doesGroupExist(group, this.parentGroup)) {
        this.parentGroup.push(group);
        this.removeChildGroups(group);
      }
    },
    removeChildGroups(group) {
      if (group.children) {
        for (let i = 0; i < group.children.length; i++) {
          this.removeChildGroups(group.children[i]);
          this.removeParentGroup(group.children[i]);
        }
      }
    },
    removeParentGroup(group) {
      const i = this.parentGroup.indexOf(group);
      if (i !== -1) {
        this.parentGroup.splice(i, 1);
        this.groupsLockedIn = false;
      }
    },
    doesGroupExist(group, children) {
      if (!children) return false;
      let exists = children.find(g => g.id === group.id);
      if (exists) return true;
      for (let i = 0; i < children.length; i++) {
        if (this.doesGroupExist(group, children[i].children)) return true;
      }
    },
    groupClass(group) {
      return {
        "added-tree-item":
          this.parentGroup.find(g => g.id === group.id) !== undefined,
      };
    },
    lockInGroups() {
      if (this.parentGroup.length > 0)
        this.groupsLockedIn = !this.groupsLockedIn;
    },
    lockInArticles() {
      if (this.addedArticles.length > 0)
        this.articlesLockedIn = !this.articlesLockedIn;
    },
    async saveOrder() {
      var payload = {
        orderId: this.orderToBeEdited.orderId,
        orderNumber: this.orderToBeEdited.orderNumber,
        articles: this.addedArticles.map(a => {
          return {
            articleId: a.id,
            maxQuantity: parseInt(a.quantity, 10),
          };
        }),
        groups: this.parentGroup.map(a => a.id),
      };
      try {
        await this.$store.$api.put("/api/orders", payload);
      } catch (error) {
        console.error(error);
        throw error;
      }

      this.$router.push({ name: "orders" });
    },
    openAddOrderDataModal() {
      this.addOrderDataKey = "";
      this.addOrderDataData = "";
      this.isAddOrderDataOpen = true;
    },
    async addOrderData() {
      const payload = {
        key: this.addOrderDataKey,
        data: this.addOrderDataData,
        isEdit: false,
      };

      await this.updateOrderData(payload);

      this.orderData = [...this.orderData, payload];

      this.isAddOrderDataOpen = false;
    },
    async updateOrderData(orderData) {
      this.loading = true;
      await this.$store.$api.post(
        `api/orders/${this.orderToBeEdited.id}/orderdata`,
        { key: orderData.key, data: orderData.data }
      );

      orderData.isEdit = false;
      this.loading = false;
    },
    async deleteOrderData(key) {
      this.loading = true;
      await this.$store.$api.delete(
        `api/orders/${this.orderToBeEdited.id}/orderData/${key}`
      );

      this.orderData = [...this.orderData.filter(x => x.key !== key)];

      this.loading = false;
    },
  },
  computed: {
    groups() {
      return this.$store.getters["GROUPS/GROUPS"];
    },
  },
  async mounted() {
    if (!this.$route.params.orderId) {
      this.$routers.push({ name: "orders" });
    }
    await this.getData();
  },
};
</script>
